import { Box, Button, Typography, Container, Paper, Grid, useTheme, useMediaQuery, Avatar, Divider, Alert, Snackbar } from "@mui/material";
import { useAuthService } from "../services/authService.jsx";
import { useUserData } from "../services/userDataService.jsx";
import { updateUserSubscribed } from "../services/stripeService.js";
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LogoutIcon from '@mui/icons-material/Logout';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StorageIcon from '@mui/icons-material/Storage';
import CircularProgress from '@mui/material/CircularProgress';

function Dashboard() {
  const { signOut, user, isAuthenticated, isLoading: isAuthLoading } = useAuthService();
  const { userData, isLoading: isUserDataLoading, error: userDataError, refetch: refetchUserData } = useUserData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const [subscriptionStatusMessage, setSubscriptionStatusMessage] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const processedSessionRef = useRef(false);

  // Check for Stripe session ID in URL
  useEffect(() => {
    const handleStripeRedirect = async () => {
      const query = new URLSearchParams(location.search);
      const sessionId = query.get('session_id');
      
      if (!isAuthLoading && sessionId && user?.userId && !processedSessionRef.current) {
        console.log(`Processing subscription for user ${user.userId} with session ${sessionId}`);
        processedSessionRef.current = true;
        
        window.history.replaceState({}, document.title, location.pathname);
        
        try {
          const result = await updateUserSubscribed(user.userId);
          console.log('User subscription updated:', result);
          
          setSubscriptionStatusMessage('Your subscription was successful! Your account has been updated.');
          
          if (refetchUserData) {
            setTimeout(() => {
              refetchUserData();
              console.log('User data refreshed after subscription update');
            }, 1000);
          }
        } catch (error) {
          console.error('Error updating subscription status:', error);
          
          try {
            console.log('Retrying subscription update after delay...');
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            const retryResult = await updateUserSubscribed(user.userId);
            console.log('User subscription updated on retry:', retryResult);
            
            setSubscriptionStatusMessage('Your subscription was successful! Your account has been updated.');
            
            if (refetchUserData) {
              setTimeout(() => refetchUserData(), 1000);
            }
          } catch (retryError) {
            console.error('Retry also failed:', retryError);
            setSubscriptionError(`We couldn't update your subscription status in our database, but your Stripe subscription is active. Please contact support if you experience any issues.`);
          }
        }
      }
    };

    handleStripeRedirect();
  }, [location, user, isAuthLoading, refetchUserData, navigate]);

  // Custom function to handle community navigation button
  const handleGoToCommunity = () => {
    if (isAuthenticated) {
      navigate("/community");
    } else {
      // If not authenticated, show a message or handle accordingly
      setSubscriptionError("You need to be logged in to access the community");
    }
  };

  const InfoItem = ({ icon, title, value, color }) => (
    <Grid item xs={12} md={6}>
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3
          }
        }}
      >
        {icon}
        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            {title}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              wordBreak: 'break-all',
              color: color || 'inherit'
            }}
          >
            {value || 'Not available'}
          </Typography>
        </Box>
      </Paper>
    </Grid>
  );

  return (
    <Container maxWidth="lg">
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100vh"
        py={{ xs: 2, sm: 4 }}
        px={{ xs: 2, sm: 3 }}
      >
        {/* Header Section */}
        <Paper 
          elevation={1} 
          sx={{ 
            p: { xs: 2, sm: 3 },
            mb: 4
          }}
        >
          <Box 
            display="flex" 
            flexDirection={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Box 
              display="flex" 
              alignItems="center" 
              gap={2}
              flexDirection={{ xs: 'column', sm: 'row' }}
              textAlign={{ xs: 'center', sm: 'left' }}
            >
              <Avatar 
                sx={{ 
                  width: { xs: 60, sm: 80 }, 
                  height: { xs: 60, sm: 80 },
                  bgcolor: theme.palette.primary.main,
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}
              >
                {(user?.attributes?.email?.[0] || 'U').toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom color="primary">
                  Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Welcome back, {user?.attributes?.['custom:displayName'] || user?.attributes?.email || user?.signInDetails?.loginId}
                </Typography>
              </Box>
            </Box>
            <Button 
              variant="outlined" 
              color="primary"
              size={isMobile ? "medium" : "large"}
              onClick={signOut}
              startIcon={<LogoutIcon />}
            >
              Sign Out
            </Button>
          </Box>
        </Paper>

        {/* Cognito User Information */}
        <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2, mb: 3 }}>
          Cognito User Information
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <InfoItem
            icon={<EmailIcon color="primary" sx={{ fontSize: 28 }} />}
            title="Email"
            value={user?.attributes?.email || user?.signInDetails?.loginId}
          />
          <InfoItem
            icon={<BadgeIcon color="primary" sx={{ fontSize: 28 }} />}
            title="User ID"
            value={user?.userId}
          />
          {user?.attributes?.email_verified && (
            <InfoItem
              icon={<VerifiedUserIcon sx={{ color: 'success.main', fontSize: 28 }} />}
              title="Email Verification"
              value="Verified"
              color="success.main"
            />
          )}
          <InfoItem
            icon={<AccessTimeIcon color="primary" sx={{ fontSize: 28 }} />}
            title="Last Sign In"
            value={new Date().toLocaleString()}
          />
        </Grid>

        {/* DynamoDB User Information */}
        <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 4, mb: 3 }}>
          DynamoDB User Information
        </Typography>
        
        {userDataError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading user data: {userDataError}
          </Alert>
        )}

        {isUserDataLoading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {userData && Object.entries(userData).map(([key, value]) => (
              <InfoItem
                key={key}
                icon={<StorageIcon color="secondary" sx={{ fontSize: 28 }} />}
                title={key}
                value={typeof value === 'object' ? JSON.stringify(value) : value}
              />
            ))}
            {(!userData || Object.keys(userData).length === 0) && (
              <Grid item xs={12}>
                <Alert severity="info">
                  No DynamoDB data available for this user.
                </Alert>
              </Grid>
            )}
          </Grid>
        )}

        {/* Subscription status messages */}
        <Snackbar
          open={!!subscriptionStatusMessage}
          autoHideDuration={6000}
          onClose={() => setSubscriptionStatusMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSubscriptionStatusMessage(null)} severity="success" sx={{ width: '100%' }}>
            {subscriptionStatusMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!subscriptionError}
          autoHideDuration={6000}
          onClose={() => setSubscriptionError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSubscriptionError(null)} severity="error" sx={{ width: '100%' }}>
            {subscriptionError}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}

export default Dashboard;